// ITEM 4b (owner, 2026-08-13) — **THE SUPPORT DESK'S LAW. MEMBERS WERE WRITING INTO A VOID.**
//
// Her reason, in her own words, for moving this item up the queue: *"MEMBERS ARE WRITING INTO A VOID
// AND THAT ENDS NOW."*
//
// >>> **MEASURED AGAINST THE LIVE TABLE AND THE LIVE DASHBOARD BEFORE A LINE WAS WRITTEN (R25/R180),
// AND THE VOID IS EXACT.** <<<
//   • **21 drafts sit `pending`** — 10 personal, 6 financial, 5 other. **NOT ONE IS `technical`**,
//     and `triage_drafts.draft` is NULL for everything that is not technical by that table's own
//     design, **so there is no composed answer for any of them.**
//   • >>> **AND THE FIRST DRAFT OF THIS HEADER READ *21 REAL PEOPLE HAVE WRITTEN IN, THE OLDEST ON
//     16 JULY*, WHICH IS FALSE AND IS CORRECTED HERE RATHER THAN QUIETLY DROPPED (R141).** <<< It
//     counted DRAFT ROWS as people. Re-measured against `contact_messages` with the harness filtered
//     the way every other count in this house filters it (W11-1: `is_harness`, R144's `.invalid`
//     address, and this table's own probe subjects): **22 rows, 15 of them harness, 7 not — and one
//     of those seven is the owner's own `admin@12thhouse.ai` row subject *testing*. SIX REAL MEMBERS
//     ARE WAITING, and the oldest is 6 AUGUST.** The 16 July rows are all probes.
//
//     **THE ITEM IS NOT SMALLER FOR IT AND THAT IS THE POINT**: six people wrote to this house and
//     nobody could answer them, which is her sentence exactly. But a number stated beside a law is a
//     receipt (R141), and this one was inflated three and a half times by counting the harness — the
//     same class of error W11-1 exists to prevent on every figure she runs the business on.
//   • **AND THE SURFACE SHE READS THEM ON HAS NO WAY TO ANSWER.** `VIEWS` in `src/commandCenter.js`
//     declares the *Wrote in* view with `acts: ["open", "reply"]` — and `OwnerMessages.tsx` renders a
//     list, a search box and an export link. **There is no reply control anywhere in it.**
//
// **THAT IS HER OWN ITEM A CLAUSE BREACHED, IN THE ONE PLACE IT COSTS A MEMBER SOMETHING:** *"no dead
// read-only lists — if she can see it she can act on it or it does not render."* The roster CLAIMS
// the act; the surface has none; twenty-one people are waiting. A capability wired to nothing has no
// error state (R38/R165), and here its absence looks exactly like a quiet week.
//
// **THIS FILE IS PURE AND IMPORTS ONLY `./triage.js`**, which is itself a shared law — so a FAST-tier
// fixture LOADS AND RUNS every decision in here, and the SERVER and her DASHBOARD read the same
// declarations. A desk where the two disagree about whether a member has been answered is a desk
// that tells her a conversation is closed while somebody waits.
import { SERVICE_ENTRY_TYPES, isDeskLane, preClassify, BREAKAGE_RE } from "./triage.js";
import { IMPROVING_NOTICE } from "./houseImproving.js";
// T114-1 — **THE ONE ANSWER TO WHETHER THIS HOUSE CAN SEND TO AN ADDRESS.** That module imports
// NOTHING, which is the entry requirement that lets this one stay loadable by a FAST-tier gate.
import { emailShapeValid } from "./emailAddress.js";
// HER CLAUSE 3's site map, DERIVED from the roster the rooms themselves are named by (R11/R31), so a
// renamed room reaches the desk's prompt without anybody remembering this file.
import { ROOM_LABELS } from "./rooms.js";

/** The rooms as a member reads them, for the one line of the addendum that teaches the house. */
export const DESK_ROOM_NAMES = Object.values(ROOM_LABELS).join(", ");

// ---------------------------------------------------------------------------
// HER FOUR FILTERS, DECLARED ONCE AND READ BY BOTH SIDES.
//
// Her clause 3: *"conversations newest first, filters (Needs me · Waiting on member · Answered ·
// Ticketed)."* The KEY travels on the wire and the LABEL is what she reads, so a label edit can never
// change which conversations a filter resolves (R28/R29 — and on this surface the cost of two
// answers is a member who reads as answered and has not been).
// ---------------------------------------------------------------------------
export const SUPPORT_STATES = [
  { key: "needs-me", label: "Needs me", note: "She wrote in and nobody has answered her." },
  { key: "waiting-on-member", label: "Waiting on member", note: "The house answered last. It is her turn." },
  { key: "answered", label: "Answered", note: "Answered, and she has come back since or let it rest." },
  { key: "ticketed", label: "Ticketed", note: "A real defect, written down on today's list." },
  // >>> **HER WROTE-IN CLAUSE (2026-08-14): CLEARED LEAVES THE DEFAULT VIEW.** <<< *"Only current and
  // necessary items render open."* A conversation she has cleared is one she has finished with, so it
  // is not a fifth thing to scroll past — it is off the screen and one filter tap away.
  { key: "cleared", label: "Cleared", note: "She has finished with it. Off the default view, here when she wants it." },
];
export const SUPPORT_STATE_KEYS = SUPPORT_STATES.map((s) => s.key);
export const supportStateValid = (k) => SUPPORT_STATE_KEYS.includes(String(k || ""));
export const supportStateLabel = (k) => SUPPORT_STATES.find((s) => s.key === String(k || ""))?.label || "";

/**
 * **WHAT STATE A CONVERSATION IS IN, AND THE ORDER OF THESE CLAUSES IS THE WHOLE LAW.**
 *
 * >>> **`ticketed` IS ASKED FIRST, AND IT IS NOT A SYNONYM FOR ANSWERED (R18).** <<< A ticket means a
 * real defect was written down for CC; it says nothing about whether the MEMBER was told anything.
 * Asked lower it would be masked by an answer, and a conversation with a live defect in it would sort
 * with the finished ones — which is the pile she never opens again.
 *
 * **AND `needs-me` IS THE DEFAULT RATHER THAN A CASE**, because the direction is the ruling
 * (R19/R8-4): a conversation wrongly in her queue costs her one glance, and one wrongly out of it is
 * a member nobody ever answers. Every state this law cannot name is hers to look at.
 */
export function supportState(conversation = {}) {
  const replies = Array.isArray(conversation.replies) ? conversation.replies : [];
  const houseSpoke = replies.some((r) => r && r.from === "house");
  // >>> **CLEARED IS ASKED FIRST, AND IT OUTRANKS EVEN A LIVE TICKET (2026-08-14).** <<< Clearing is
  // her own act on her own screen — the one state on this roster a PERSON decided rather than the
  // house — so it is the last word about whether a row is still current. Asked lower, a ticket she
  // has finished with would sit in the queue for ever, which is the pile her clause is about.
  if (conversation.clearedAt) return "cleared";
  if (conversation.ticketId) return "ticketed";
  if (!houseSpoke) return "needs-me";
  // The member came back AFTER the house answered, so it is the house's turn again.
  const lastHouse = replies.filter((r) => r?.from === "house").map((r) => r.at).sort().pop() || "";
  const lastMember = replies.filter((r) => r?.from === "member").map((r) => r.at).sort().pop() || "";
  if (lastMember && lastHouse && String(lastMember) > String(lastHouse)) return "needs-me";
  return conversation.closed ? "answered" : "waiting-on-member";
}

/** Her filter, applied. An unrostered key shows EVERYTHING rather than nothing (R19's direction). */
export function filterConversations(list = [], filterKey = "") {
  const rows = Array.isArray(list) ? list : [];
  if (!supportStateValid(filterKey)) return rows;
  return rows.filter((c) => supportState(c) === filterKey);
}

// ---------------------------------------------------------------------------
// WHAT HER DESK IS SHOWING, AND THE ORDER OF THE THREE NARROWINGS IS THE LAW.
//
// Her clause 2 gives the IT sheet a **VIEW CONVERSATION** control, and a door has to open onto its
// own row. Her clause 3 gives the desk her four filters and it already had a search box. Those three
// can all be narrowing at once, so *which one outranks which* is a decision rather than an ordering
// accident — and getting it wrong is the same dead door this whole item is about.
// ---------------------------------------------------------------------------

/**
 * **THE SEARCH, MOVED OUT OF THE DASHBOARD SO A GATE RUNS IT.** Its own stated law in App.tsx was
 * *"matching is over everything she can SEE on the card — a search that only reads the subject line
 * leaves her scrolling for the message she remembers by its words"*, and it read five fields.
 *
 * **THE CARD GREW A TRANSCRIPT AND THE SEARCH DID NOT FOLLOW** (R68: a stated law that quietly
 * stopped being true). Her clause 2 puts every reply on that card, so the words she remembers are as
 * likely to be the ANSWER as the question — and a search that silently under-matches reads exactly
 * like a conversation that is not there.
 */
export function deskMatches(conversation = {}, needle = "") {
  const q = String(needle || "").trim().toLowerCase();
  if (!q) return true;
  const c = conversation || {};
  const replies = Array.isArray(c.replies) ? c.replies : [];
  return [c.name, c.email, c.subject, c.message, c.reason, ...replies.map((r) => r?.body)]
    .filter(Boolean).join(" ").toLowerCase().includes(q);
}

/**
 * **WHAT SHE IS TOLD WHEN A DOOR OPENS ONTO A CONVERSATION THE DESK CANNOT SHOW HER.** Declared once,
 * and it names no machinery (S1-A/R117): the reason is a window, not a table.
 */
export const DESK_FOCUS_MISSING_NOTE =
  "That conversation is older than the desk reads back, so it is not on this screen.";

/**
 * **THE DESK, NARROWED.**
 *
 * >>> **A FOCUS OUTRANKS BOTH HER FILTER AND HER SEARCH, AND THAT IS THE WHOLE CLAUSE.** <<< The tap
 * came from a TICKET row, so the destination must contain the conversation that ticket is about.
 * Ranked below the filter, a `View conversation` tapped while she is standing on *Needs me* lands her
 * on a screen that does not contain it — **a door that opens onto nothing**, which is her own ITEM A
 * clause broken by the control built to satisfy it.
 *
 * >>> **AND A FOCUS THAT NAMES NOTHING IS ITS OWN FACT, NEVER AN EMPTY DESK (R18/R64).** <<< The
 * conversation read is BOUNDED (the store reads back a window) and a ticket's link survives forever,
 * so an old ticket really can point at a conversation this screen does not hold. Answering that with
 * an empty list tells her nobody is waiting, which is the one thing this desk may never say — so the
 * unfocused list comes back WITH a flag that says why she is not looking at what she tapped.
 */
/**
 * >>> **WHAT HER DESK SHOWS WHEN SHE HAS ASKED FOR NOTHING IN PARTICULAR (2026-08-14).** <<<
 *
 * Her clause: *"only current and necessary items render open."* Every state but `cleared`, DERIVED
 * from the roster rather than listed, so a sixth state joins the default view by existing and the one
 * exclusion is stated in exactly one place. **An explicit `cleared` filter still shows them** — that
 * is the difference between putting something away and hiding it (R18), and a screen that could not
 * show her what she cleared would be a screen she cannot audit.
 */
export const DEFAULT_DESK_STATES = SUPPORT_STATE_KEYS.filter((k) => k !== "cleared");
export const deskDefaultShows = (state) => DEFAULT_DESK_STATES.includes(String(state || ""));

export function deskView(list = [], { search = "", filter = "", focusId = "" } = {}) {
  const all = Array.isArray(list) ? list : [];
  const inDefaultView = (c) => (supportStateValid(filter) ? true : deskDefaultShows(supportState(c)));
  const narrowed = () => filterConversations(all, filter).filter((c) => deskMatches(c, search)).filter(inDefaultView);
  const id = String(focusId || "").trim();
  if (!id) return { rows: narrowed(), focused: false, missing: false };
  const one = all.filter((c) => String(c?.id || "") === id);
  if (one.length) return { rows: one, focused: true, missing: false };
  return { rows: narrowed(), focused: false, missing: true };
}

// ---------------------------------------------------------------------------
// HER REPLY, AND WHAT IT IS NOT ALLOWED TO BE.
// ---------------------------------------------------------------------------

/** The most a reply may carry. Her own inbox composer's cap, so one message is one message. */
export const SUPPORT_REPLY_MAX = 2000;

/**
 * **IS THERE A REAL MEMBER AT THE OTHER END OF THIS CONVERSATION?** Presence, never truthiness
 * (R53/R136/R137): this arrives off JSON, and `Number("")` is 0, so a coerced id would address a
 * reply to member zero.
 *
 * **AND THE FIRST DRAFT LET `"0"` THROUGH** — the string branch asked only for digits, so the one
 * value this clause exists to refuse was the one it accepted. Its own fixture caught it. Both
 * branches now ask the same question: a REAL POSITIVE INTEGER.
 *
 * Lifted out of `supportReplyViolations` by T114-1 (R155/R160-3), because the ROUTE law below has to
 * ask the identical question and two spellings of *is this a member* is two answers to which carrier
 * a reply takes (R28/R29).
 */
export function supportReplyMemberId(memberId = null) {
  const id = typeof memberId === "string" && /^\d+$/.test(memberId.trim()) ? Number(memberId.trim()) : memberId;
  return Number.isSafeInteger(id) && id > 0 ? String(id) : "";
}

// >>> **T114-1 (her order, 2026-08-14) — A WRITER WITH NO ACCOUNT IS ANSWERED BY EMAIL.** <<<
//
// Her item: *"her reply control answers a writer with no account by EMAIL through the armed rail,
// dressed in the shell, ledgered on the conversation."*
//
// **THE CONTACT FORM IS OPEN TO PEOPLE WITH NO ACCOUNT BY ITS OWN MIGRATION'S DESIGN** — `user_id`
// is nullable — and until now the desk answered every one of them with a sentence telling the owner
// to leave her dashboard and write to the address herself. That is her own ITEM A clause (*"if she
// can see it she can act on it or it does not render"*) satisfied by removing the control instead of
// building it.
//
// >>> **AND THE MEASUREMENT CORRECTS THE ITEM'S OWN PREMISE, WHICH IS REPORTED RATHER THAN QUIETLY
// USED (R19/R141).** <<< The pointer that opened this item says *"15 of 24 conversations genuinely
// have no member row, so this is real"*. The count is exact and **not one of the fifteen is a real
// person**: seven are `.invalid` harness seats and eight are July probe rows on `example.com` and
// `12house.local`. **The backlog this unblocks today is ZERO writers.** It is still built, because
// the door is open to visitors and the next one who uses it must not meet a dead end — but the
// number is not inflated to justify the round (`scripts/t114-1-premeasure.mjs` prints every row).

/**
 * **THE SHAPE A MAIL RAIL CAN REALLY SEND TO.**
 *
 * >>> **THE TEST IS `emailShapeValid`'s AND THIS FILE DOES NOT OWN A SECOND ONE (R28/R29).** <<< The
 * first draft of this law carried its own regex, and the signup round landed a shared one the same
 * day — with a clause no private copy had: **it refuses the characters that break a mail header**,
 * which is the exact defect that made Gmail answer `Invalid To header` on the owner's own address.
 * Two answers to *can this house send to it* would have meant this desk offering a composer for an
 * address the signup form had already refused.
 *
 * **`.invalid` IS REFUSED HERE AND NOT THERE, AND THAT IS A DIFFERENT QUESTION RATHER THAN A
 * DISAGREEMENT (R18).** The signup form asks whether a person may TYPE an address; this asks whether
 * the house may SEND to one. RFC 2606 reserves `.invalid` as never-resolvable, and it is R144's
 * harness convention, which ITEM 12's test ring exists to keep out of every send path in this house
 * (a trace once notified 118 real members).
 */
export function supportReplyMailable(email = "") {
  const address = String(email || "").trim();
  if (!address || address.length > 254) return "";
  if (!emailShapeValid(address)) return "";
  if (/\.invalid$/i.test(address)) return "";
  return address;
}

/**
 * **WHICH CARRIER A REPLY TAKES, ASKED ONCE AND READ BY BOTH SIDES.**
 *
 * `inbox` · `email` · `""`. The screen draws the control this answers and the door performs it, so
 * her desk can never offer a reply the server would refuse, nor refuse one it would happily send
 * (R28/R29 — R15's law, on a support desk instead of a file picker).
 *
 * >>> **THE INBOX OUTRANKS THE ADDRESS, AND THE ORDER IS THE LAW.** <<< A member with an account is
 * answered where she can read it inside the house: it needs no mail rail, it cannot bounce, it cannot
 * be filtered into somebody's spam folder, and it is what her desk has always done. Email is the
 * carrier for the person who has no other one — never a second way to reach somebody who already has
 * a room here, which would be two answers to *where did the house reply* on the one record her audit
 * reads.
 *
 * **AND AN UNMAILABLE ADDRESS IS ITS OWN FACT, NEVER "REPLY BY EMAIL" (R18).** *No account*, *no
 * usable address* and *a reply that failed* are three different things; collapsing the middle one is
 * how a conversation gets marked answered with nothing having left the house.
 */
export function supportReplyRoute({ memberId = null, email = "" } = {}) {
  // **DERIVED, NEVER SPELLED TWICE.** The carriers law below is the one answer; this is its FIRST
  // one, which is what a screen draws and what the ledger files as the primary. Two spellings of
  // *where does a reply go* is how the door and her desk come to disagree (R28/R29).
  return supportReplyCarriers({ memberId, email })[0] || "";
}

/**
 * >>> **EVERY CARRIER A REPLY TAKES — AND AFTER 2026-09-03 THAT IS BOTH OF THEM, NOT ONE.** <<<
 *
 * **THIS SUPERSEDES THE CLAUSE ABOVE IT**, which read *the inbox outranks the address... never a
 * second way to reach somebody who already has a room here*. That was right on the day it was written
 * and it was overtaken by the owner's HEAL-NOTIFICATION AMENDMENT (2026-09-02): *"every heal line
 * goes BOTH WAYS — the site inbox message AND an email, dressed in the shell, with a gold button
 * deep-linking to exactly where the healed thing lives."*
 *
 * **AND FOUR TICKETS PAID FOR THE CHANGE BEFORE IT WAS MADE.** #176 and #177 were answered twice into
 * an inbox the member had told us twice she could not read; #187 had to be emailed by hand for the
 * same reason; #179's line was called on both rails side by side by a script, because the desk itself
 * could only pick one. **A member who cannot read her inbox got NOTHING AT ALL from this desk.**
 *
 * The answers: both carriers, the inbox alone, the email alone, or nothing. **THE ORDER IS THE LAW:**
 * the inbox is first because it is service messaging that lands regardless, and the email is second
 * because it asks consent first (`mailable`, a database fact and therefore a PARAMETER — this module
 * reads nothing, which is what lets a FAST fixture run it).
 *
 * **AND `mailable` DEFAULTS TO TRUE ON PURPOSE.** A caller that cannot answer the consent question
 * has not proved she opted OUT, and a screen only needs to know a carrier exists. The DOOR passes the
 * real answer, which is where consent is actually enforced.
 */
export function supportReplyCarriers({ memberId = null, email = "", mailable = true } = {}) {
  const carriers = [];
  if (supportReplyMemberId(memberId)) carriers.push("inbox");
  if (supportReplyMailable(email) && mailable !== false) carriers.push("email");
  return carriers;
}

/**
 * **WHERE THE EMAIL'S GOLD BUTTON LANDS HER, AND IT IS THE THREAD ITSELF WHEN THERE IS ONE.**
 *
 * Her deep-link law: *the button lands her ON the thing, signed in or through sign-in, never the home
 * page.* For a desk reply the thing is the conversation the reply is sitting in, so the button opens
 * `/inbox?thread=<id>` — the same shape `HouseInbox` composes for a notification tap, because two
 * spellings of *how you open a thread by URL* is how one of them rots (R28/R29).
 *
 * **A REPLY WITH NO THREAD (a writer with no account) GETS NO BUTTON**, and that is the honest answer
 * rather than a button to a page she cannot open: there is nowhere in this house that is hers yet,
 * and a gold JOIN button under an answer to her problem would turn a support reply into a sales mail
 * — the mailer's own standing ruling, which this change does not touch.
 */
export function supportReplyDeepLink(threadId = "") {
  const id = String(threadId || "").trim();
  return id ? `/inbox?thread=${encodeURIComponent(id)}` : "";
}

/** The words on that button. One declaration, so the mail and a fixture read the same label. */
export const SUPPORT_REPLY_CTA = "Read it in your messages";

/**
 * **WHAT REFUSES A REPLY BEFORE IT IS SENT.**
 *
 * **AN EMPTY REPLY IS NOT A REPLY (R18):** *nothing typed* and *a reply that failed* are different
 * facts, and only the first is the member's own doing. And a reply with nobody to receive it is the
 * one failure that would mark a conversation ANSWERED while nobody was told anything — which is this
 * whole item's defect wearing a green tick.
 *
 * >>> **`no-member` BECAME `no-recipient` WITH T114-1, AND THE RENAME IS THE POINT RATHER THAN
 * TIDYING.** <<< The fact this clause refuses changed: it is no longer *there is no member* — a
 * writer with no account is answerable now — it is *there is nobody this reply could reach by any
 * carrier this house has*. A key still spelling `no-member` would read, to the next session, as a
 * law that had not been migrated (R68).
 */
export function supportReplyViolations({ body = "", memberId = null, email = "" } = {}) {
  const bad = [];
  const text = String(body || "").trim();
  if (!text) bad.push("no-body");
  if (text.length > SUPPORT_REPLY_MAX) bad.push("too-long");
  if (!supportReplyRoute({ memberId, email })) bad.push("no-recipient");
  return bad;
}

/**
 * **WHAT SHE IS TOLD WHERE THERE IS NO WAY TO ANSWER AT ALL.** The address is offered where there is
 * one, because a dead end that names the next step is not a dead end — and a row whose address cannot
 * receive mail says THAT rather than pretending an account is the missing thing (R18).
 */
export function supportNoReplyNote({ email = "" } = {}) {
  const address = String(email || "").trim();
  if (!address) return "This came in with no address and no account, so there is no way to answer it from here.";
  return `That address cannot receive mail, so there is no way to answer it from here. It came in as ${address}.`;
}

/**
 * **AND WHERE THE RAIL IS OFF, THE REASON IS NAMED AND THE ADDRESS IS STILL OFFERED.** A composer
 * that draws and always refuses is her ITEM A clause broken the other way round, so the control is
 * not drawn — but *the mail rail is off* and *there is nobody to answer* are different facts and she
 * is sent to two different places by them (R18).
 */
export function supportMailOffNote({ email = "" } = {}) {
  const address = String(email || "").trim();
  return `The mail rail is not switched on, so the house cannot email her from here yet.`
    + (address ? ` She wrote in from ${address}.` : "");
}

/**
 * **A REPLY IS A `reply` ENTRY ON THE ONE APPEND-ONLY HISTORY, NOT A TABLE OF ITS OWN.**
 *
 * `member_service_log` is D4's table and its own migration says in as many words that a future
 * support layer *"reads the history AS DATA and appends its own entries THROUGH THE SAME DOOR"*.
 * Minting a `support_replies` table beside it would be a second history of what this house has said
 * to a member (R28/R29), and the one that drifts is the one her audit reads.
 */
export const SUPPORT_REPLY_ENTRY = "reply";

/**
 * **AND THE TYPE MUST BE ON THE ROSTER, OR EVERY REPLY IS REFUSED SILENTLY.** `logService` validates
 * `entry_type` against `SERVICE_ENTRY_TYPES`, so a reply typed with an unrostered value writes
 * nothing and answers nothing — ITEM 9 paid exactly this price learning that `TICKET_SOURCES` had to
 * name `contact` before the contact form could file. Held as a biconditional rather than asserted.
 */
export const supportReplyTypeRostered = () => SERVICE_ENTRY_TYPES.includes(SUPPORT_REPLY_ENTRY);

/**
 * **WHAT THE MEMBER IS TOLD, AND IT NAMES NO MACHINERY (S1-A/R117).** Her reply is her own words; this
 * is only the line that carries them into a room where a message needs a sender. Declared once so the
 * DM and the logged entry cannot drift into two different accounts of the same act.
 */
export const SUPPORT_REPLY_SUMMARY = "The house answered a message she sent in.";

// ---------------------------------------------------------------------------
// SD4's REMAINING HALF (owner's clause 4, 2026-08-13) — **AND HER REPLY CONTINUES THE SAME LOGGED
// CONVERSATION.** Her words: *"the member gets the thread in her INBOX so she holds the record and
// can reply, replies continuing the same logged conversation."*
//
// >>> **MEASURED BEFORE A LINE WAS WRITTEN (R25/R180), AND THE LAW THAT NEEDS IT WAS ALREADY
// WRITTEN AND COULD NEVER FIRE.** <<< `supportState` above carries *"The member came back AFTER the
// house answered, so it is the house's turn again"* and compares `lastMember` against `lastHouse` —
// and **nothing in this repository has ever written a `reply` entry with `actor: "member"`**, so
// that branch is unreachable, `from: "member"` is a shape the store maps and never receives, and a
// member who answers the house's reply in her own inbox is invisible to the desk. **She is answered
// once and then writes into exactly the void this whole item exists to end.**
//
// **THE LINKAGE IS THE THREAD, AND IT IS ALREADY ON THE ROW.** `sendSupportReply` writes
// `detail.threadId` on the inbox branch, so *which conversation did the house answer here* is a fact
// the append-only history already holds — no migration, no column, and no second record of what a
// support conversation is (R28/R29).
// ---------------------------------------------------------------------------

/**
 * **THE ACTOR IS `member`, AND IT IS THE ONE STRING THE TRANSCRIPT'S SIDES ARE DECIDED BY.** The
 * store maps `r.actor === "member" ? "member" : "house"`, so a row written under any other spelling
 * renders HER WORDS AS THE HOUSE'S on the one record her audit reads, and `supportState` reads
 * `waiting-on-member` for ever over a member who has already come back. Declared here, beside the
 * entry type, rather than typed at the write site.
 */
export const SUPPORT_INBOUND_ACTOR = "member";

/** The one line the history renders for her half. Composed here for `SUPPORT_REPLY_SUMMARY`'s reason. */
export const SUPPORT_INBOUND_SUMMARY = "She answered in her inbox.";

/**
 * **AND THE ONE WORD THE TRANSCRIPT SAYS ABOUT A REPLY THE ROOM'S GATE HELD.** It names what
 * happened rather than the machinery that did it (S1-A/R117 — no gate name, no visibility key), and
 * it lives here rather than in the markup so a gate can ask for it (R8/R95).
 */
export const SUPPORT_HELD_MARK = "Held by the room";

/**
 * **WHAT A MEMBER'S INBOX MESSAGE HAS TO BE BEFORE IT JOINS A SUPPORT CONVERSATION.**
 *
 * >>> **THE FIRST CLAUSE IS THE LEAK CLAUSE AND IT IS WHY THIS IS A LAW RATHER THAN AN `if`.** <<<
 * With no support reply logged against this thread there is no conversation to continue, and the
 * message is an ORDINARY private DM between two members. Copying one of those into
 * `member_service_log` would put a private conversation on the work-order surface she pastes into CC
 * — **permanently, because that table has a writer, a reader and no editor.** So the contact id is
 * REQUIRED and is never defaulted.
 *
 * >>> **THE SECOND IS THE OWNER, AND ITS FAILURE IS TWO DEFECTS AT ONCE.** <<< The house's half is
 * written by the desk's own door; an owner message in that thread logged here would attribute HER
 * words to the member on the transcript AND flip the conversation to `needs-me` the instant she
 * answers. **The owner replying from her inbox rather than from the desk is a different fact and is
 * deliberately NOT logged by this round** (DECISIONS SD4-2): a conversation wrongly left in her
 * queue costs one glance, and one wrongly out of it is a member nobody answers — `supportState`'s
 * own stated direction (R19/R8-4).
 *
 * **A HELD MESSAGE STILL JOINS, AND IT IS MARKED.** LR1 ruling 1 hides it from everyone but its
 * author — and the owner is the one seat this house lets read held content, which is LR19's whole
 * round. Dropping it would leave the member the gate held in the void, which is the sentence this
 * item is about; surfacing it UNMARKED would be LR19's own reported defect one surface over (*"she
 * reads it like everyone sees it and has no idea it is held"*). So the fact rides the entry.
 *
 * **AND A MESSAGE WITH NO WORDS IS NOT NOTHING** (LR16: a message may be pictures alone). The body
 * falls back to the ROOM'S OWN excerpt — handed in, never re-derived here — so the desk and the bell
 * say the same thing about the same message (R28/R29). With neither words nor an excerpt there is
 * nothing to record and the answer is `null`.
 *
 * Returns the entry to write, or `null`. Three outcomes rather than a boolean (R18): the caller
 * needs the BODY it resolved as well as the verdict, and re-deriving it at the write site is how the
 * two come to disagree.
 */
export function supportInboundReply({ contactId = "", senderIsOwner = false, body = "", excerpt = "", held = false } = {}) {
  const id = String(contactId || "").trim();
  if (!id) return null;
  if (senderIsOwner === true) return null;
  const text = String(body || "").trim() || String(excerpt || "").trim();
  if (!text) return null;
  // Presence, never truthiness (R53/R136/R137): this arrives off a verdict comparison and only a real
  // `true` is a hold. A coerced value would mark an ordinary reply as machine-held on her screen.
  return { contactId: id, body: text, held: held === true };
}

// ---------------------------------------------------------------------------
// SD3 — **THE TRIAGE LAW: WHAT THE DESK *DOES*.**
//
// >>> **MEASURED BEFORE A LINE WAS WRITTEN (R25/R180), AND MOST OF THIS ALREADY EXISTED IN A
// DIFFERENT SHAPE.** <<< `src/triage.js` has classified an incoming message since ITEM 12 —
// `preClassify` forces distress, identity and money to her pile and `classify` folds the engine in
// under it. **What it answers is *WHAT KIND OF MESSAGE IS THIS*.** Her SD3 clause asks a different
// question: *WHAT DOES THE DESK DO ABOUT IT* (R18). A second classifier would be a second answer key
// on the one path where being wrong is worst, so this law CONSUMES that verdict rather than
// re-deriving it, and the whole money/distress/identity vocabulary stays declared exactly once.
//
// **HER THREE OUTCOMES, IN HER OWN WORDS:**
//   (a) a how-do-I question or a lost member -> **ANSWER warmly and completely, teach where the thing
//       lives, NO TICKET**;
//   (b) a real defect it cannot solve -> **FILE A REAL TICKET** with her name, her words and a
//       one-line summary of what was tried, **and tell her plainly it is filed**;
//   (c) anything payment- or refund-shaped -> **THE OWNER'S ATTENTION, NEVER AUTO-ACTIONED**.
// ---------------------------------------------------------------------------

/**
 * The three, declared once with the sentence each one is. The KEY travels and the LABEL is what she
 * reads on the conversation row, which is the same split her four filters already make.
 */
export const SUPPORT_OUTCOMES = [
  { key: "answered", label: "Answered", ticket: false, owner: false,
    note: "A how-do-I or a lost member. The desk answered it and taught her where the thing lives." },
  { key: "ticketed", label: "Ticketed", ticket: true, owner: false,
    note: "A real defect the desk could not solve. Written down for CC, and she was told plainly." },
  { key: "owner", label: "For her", ticket: false, owner: true,
    note: "Money, identity or distress. It goes to the owner and the desk actions nothing." },
  // >>> **HER SCOPE CLAUSE'S OWN OUTCOME (2026-08-14). *ONE POLITE LINE AND A CLOSE.*** <<<
  //
  // *"Off-topic attempts get ONE polite line and a close."* That is neither `answered` (the desk
  // taught her nothing and there was nothing to teach) nor `ticketed` (nothing is broken) nor `owner`
  // (there is nothing for her to rule on) — it is a fourth fact, and giving it its own key is what
  // keeps her NEEDS ME pile honest: a stranger asking this desk for a tarot reading must not sit in
  // the queue of people awaiting her, and must not be counted as a member the house helped.
  { key: "closed", label: "Closed", ticket: false, owner: false,
    note: "Not a site matter. The desk said one line and closed it." },
];
export const SUPPORT_OUTCOME_KEYS = SUPPORT_OUTCOMES.map((o) => o.key);
// **THERE IS NO `supportOutcome(key)` LOOKUP, AND THE ABSENCE IS R165 OBEYED RATHER THAN DODGED.**
// One was written here and DELETED with its reason on the round that wrote it, because nothing called
// it: the label a surface needs comes off the roster it is already iterating, and a lookup kept warm
// is a capability a later round wires by finding it. R165 named it on this round own first gate run.

/**
 * **WHICH CATEGORIES THE DESK MAY ANSWER AT ALL, READ OFF THE ROSTER RATHER THAN TYPED** (R11/R31).
 *
 * >>> **THE FIRST DRAFT DERIVED THIS FROM `isAutoLane` AND THAT WAS WRONG — CAUGHT BY DRIVING IT
 * RATHER THAN BY READING IT (R25/R129).** <<< `auto` answers *may the house draft a WORK ORDER for
 * this lane*, which is true of `technical` alone. Her SD3(a) is **a how-do-I question or a lost
 * member**, and `classify` deliberately keeps those OUT of `technical` (the `CRAFT` demotion), so
 * they land in `other`. Reusing `auto` therefore sent **every question this desk exists to answer**
 * to the owner's pile, and the desk answered nothing at all — a perfectly green law implementing the
 * opposite of her clause. Two questions, two flags (R18).
 */
export const deskMayAnswer = (category) => isDeskLane(category);

// ---------------------------------------------------------------------------
// >>> **HER SCOPE CLAUSE, ABSOLUTE: THIS DESK ANSWERS SITE MATTERS AND NOTHING ELSE (2026-08-14).**
//
// *"NOTHING else: no readings, no Lala-style conversation, no spiritual guidance, no chat."*
//
// **IT IS THE MODEL'S OWN STRUCTURAL VERDICT AND NEVER A KEYWORD LIST, AND THAT IS THE ROUND.** A
// word list is defeated by the next rephrasing and convicts the honest sentence beside it — this
// house has paid for both directions in one week (`\britual\b` convicting *spiritual*, and the
// catalog round's denial gate convicting nineteen healthy teaching turns). The question *is this
// person asking about using the site, or asking the house for a reading* is a question about SHAPE,
// which is what a model is for; the deterministic floor below it is what refuses a DRAFT that
// wandered anyway (THE GATE LAW: a prompt prevents, a gate enforces).
// ---------------------------------------------------------------------------
export const DESK_SCOPE_SITE = "site";
export const DESK_SCOPE_OFF = "off-topic";
export const DESK_SCOPES = [DESK_SCOPE_SITE, DESK_SCOPE_OFF];

/**
 * **THE ONE LINE AND THE CLOSE.** Her energy, in her own register: warm, brief, final, and it names
 * what this desk IS for rather than only what it is not — a refusal that offers nothing is the PATHS
 * PROTOCOL cruelty ITEM 5 exists about, and a member who came to the wrong door should still leave
 * knowing where the right one is.
 *
 * **IT INVITES NOTHING**, which is the *final* half of her clause: no question, no offer to continue,
 * nothing a conversation can be built on. That is deliberate and it is what makes it a close.
 */
export const DESK_CLOSE_LINE =
  "This desk is just for help with the site itself, love. Everything else lives with Lala in the house. I hope you find what you are looking for.";

/**
 * **HER THANK-YOU LINE, VERBATIM.** *"Thank-yous get 'You're welcome, enjoy the site.'"* — her exact
 * words, kept as she wrote them (R125: copy is hers), and declared here so the desk cannot answer a
 * two-word thank-you with a paragraph.
 */
export const DESK_THANKS_LINE = "You're welcome, enjoy the site.";

/**
 * >>> **#141 (2026-08-20): THE HOUSE WAS SPEAKING AS HER, AND NOTHING ON THE MESSAGE SAID SO.** <<<
 *
 * **HER TICKET:** *"members' IT questions are being answered from my personal chat in my name, and
 * members are replying to me as technical support."*
 *
 * **MEASURED BEFORE A LINE WAS WRITTEN (R25/R180), AND THE PREMISE HOLDS HARDER THAN HER SENTENCE.**
 * On the live table: **19 support replies exist and 18 of them were sent by `actor: "agent"`** — the
 * unattended desk — **and every one is authored in `lr_messages` by her own member row.** So it is
 * not that she answered from the wrong place; it is that **the machine answered, as her, into her
 * personal DM thread**, and **13 members have written back into those threads** believing they were
 * talking to her.
 *
 * **THE CARRIER IS CORRECT AND IS NOT WHAT MOVES.** SD4's clause is that the member gets the thread
 * in her INBOX so she holds the record and can reply, and `noteInboundSupportReply` already carries
 * that reply back to the desk. What was missing is that **nothing on the message told anybody which
 * of the two it was** — a support answer and a personal message from her wear one face (R18), on the
 * one surface where only she can tell the difference and only after the fact.
 *
 * **>>> ONLY THE AGENT'S ANSWER IS MARKED, AND THAT IS THE CLAUSE WITH TEETH. <<<** A reply she typed
 * at her own desk **really is from her** and marking it would be the house telling a member that
 * Chaela's own words came from a desk — this round's defect pointing the other way (R141: a receipt
 * for something that did not happen). `actor` is the only field that separates them and it is already
 * on the row.
 *
 * **AND IT IS THE INBOX CARRIER ONLY, DECLARED RATHER THAN LEFT AS AN ABSENCE (R17/R160).** A mail
 * already announces the house in its own From line and its shell (T114-1), so the defect this line
 * answers does not exist there; appending it would be a second signature under one that already
 * says so.
 *
 * **IT GOES AT THE END, WHICH IS NOT A STYLE CHOICE.** A message push carries an EXCERPT taken from
 * the front of the body, so an attribution at the top would lead every support notification with
 * housekeeping and bury the answer she is waiting for.
 *
 * **IT NAMES NO PERSON** (LR1 ruling 3: the only name any surface renders is the room first-name
 * `publicIdentity` builds, so a literal naming her here would be a second answer to what she is
 * called), **NAMES NO MACHINERY** (S1-A/R117, and `DESK_INTERNALS_RE` is the desk's own wall),
 * carries **no address** (`DESK_URL_RE`) and **no em dash**.
 *
 * **AND IT TELLS HER WHERE HER REPLY GOES RATHER THAN ASKING HER NOT TO WRITE BACK**, because that
 * is the half of her ticket about members answering support in her inbox — and it is TRUE rather
 * than a hope: SD4's inbound half is built, wired and has 13 rows behind it.
 *
 * **ENGLISH, BY THIS DESK'S OWN PRECEDENT AND SAID SO (R17/R160).** `DESK_CLOSE_LINE` and
 * `DESK_THANKS_LINE` are the house's own words on this desk and are English; the desk resolves no
 * member language anywhere, so inventing one here would be a second answer to a question this door
 * has never asked.
 */
export const DESK_HOUSE_MARK =
  "This answer is from the 12th House support desk. Anything you write back here comes straight to the desk.";

/** The desk's own separator. Two newlines, so the mark reads as its own line under the answer. */
export const DESK_MARK_SEPARATOR = "\n\n";

/**
 * **THE ONE SPELLING OF *THE DESK SENT THIS*.** The store stamps it, the law reads it and the ledger
 * files under it, so a second spelling cannot make a marked message unmarked (R28/R29).
 */
export const DESK_AGENT_ACTOR = "agent";

/**
 * **WHAT REALLY LEAVES THE DOOR, AS A PURE LAW A GATE RUNS** (R8/R95: a composition typed inside the
 * store is one no fixture can drive).
 *
 * Every default is the SAFE one: an unknown actor is treated as HER (unmarked), because marking a
 * message she wrote is the direction that puts a false receipt in front of a member, while missing a
 * mark costs exactly what shipped yesterday (R19). An empty body is handed back untouched, so the
 * refusal above this stays the one that decides.
 *
 * **IT IS IDEMPOTENT**, because a body already carrying the mark is one a retry must not sign twice.
 */
export function deskDeliveredBody(body = "", { actor = "owner", via = "inbox" } = {}) {
  const text = String(body ?? "");
  if (!text.trim()) return text;
  if (actor !== DESK_AGENT_ACTOR) return text;
  if (via !== "inbox") return text;
  if (text.includes(DESK_HOUSE_MARK)) return text;
  return `${text}${DESK_MARK_SEPARATOR}${DESK_HOUSE_MARK}`;
}


/**
 * >>> **THE DESK'S VERDICT. THE ORDER OF THESE THREE CLAUSES IS THE ENTIRE LAW.** <<<
 *
 * **(c) IS ASKED FIRST AND IT OUTRANKS EVERYTHING**, because her clause says *never auto-actioned*
 * and a lane the desk may not act on is one it may not answer INTO either. Asked lower, a member
 * writing *"my card was double charged and the page is broken"* trips the breakage vocabulary, reads
 * as a defect, and gets a cheerful ticket receipt about her MONEY — which is the one wrong answer on
 * this desk that costs trust rather than time.
 *
 * **(b) IS THE MIDDLE ONE AND IT IS REACHED ONLY BY FAILING TO ANSWER.** `resolved` is the desk's own
 * honest report of whether it solved the thing — *"a real defect it cannot solve"* is her wording, so
 * the ticket is a consequence of the attempt rather than of the category. A desk that ticketed every
 * technical message would file a work order for *"how do I open my saved readings"*.
 *
 * **(a) IS THE DEFAULT, AND THAT DIRECTION IS THE OPPOSITE OF `supportState`'s ON PURPOSE (R19).**
 * There, an unknown sorts INTO her queue, because a conversation wrongly in front of her costs a
 * glance. Here, an unknown must NOT invent a ticket: a false work order on the sheet CC works from
 * is somebody's afternoon spent on a defect that does not exist (R141), and the member has still
 * been answered. So the fail-safe is *answer and do not file*, and anything the desk could not
 * answer at all is `owner` rather than `answered` — which is the clause below.
 *
 * @param {{category?: string, resolved?: boolean}} verdict
 */
export function deskOutcome({ category = "", resolved = false, scope = "" } = {}) {
  // (c) — her pile. Anything the desk may not act on, and anything nobody classified.
  if (!deskMayAnswer(category)) return "owner";
  // >>> **HER SCOPE WALL, AND ITS POSITION IS THE WHOLE OF ITS SAFETY (2026-08-14).** <<<
  //
  // It is asked BELOW (c) and above everything else. Below, because a member writing *"you charged me
  // twice and also what does the moon in my seventh house mean"* is a MONEY conversation that a model
  // could honestly read as off-topic — and closing that with one polite line is the worst answer this
  // desk can give. Above the rest, because once it is her business at all, *is this a site matter* is
  // the first question and everything under it assumes the answer is yes.
  //
  // **`scope === "off-topic"` AND NEVER A TRUTHY READ** (R53/R136/R137): this crosses a JSON boundary
  // from the model's own report, and a coerced value would close a real support question with one
  // line and no ticket — silently, and the member never writes in again.
  if (String(scope || "") === DESK_SCOPE_OFF) return "closed";
  // (a) — solved, so nothing is written down.
  // **`resolved === true` AND NEVER A TRUTHY READ** (R53/R136/R137): this flag crosses a JSON
  // boundary from the model's own report, and `Boolean("false")` is `true` — a coerced value here
  // closes a real defect as answered and the member never hears about it again.
  if (resolved === true) return "answered";
  // (b) — tried and could not.
  return "ticketed";
}

/**
 * **WHAT THE MEMBER IS TOLD, PER OUTCOME — AND THE TICKET SENTENCE NAMES THE NUMBER.**
 *
 * Her clause 1 is that the number is *visible EVERYWHERE the ticket appears*, and the member's own
 * thread is one of the four surfaces she names. `ticketRef` is the ONE spelling (`src/itTickets.js`),
 * so the sheet row, the CC box and this sentence cannot disagree about which ticket this is.
 *
 * **A TICKET WITH NO NUMBER STILL GETS THE SENTENCE, WITHOUT A NUMBER IN IT** (R141/W12-5): the row
 * is real and she is owed the telling, and a fabricated reference is worse than none — `ticketRef`
 * already answers `""` for a row with no id and this composes around that rather than printing an
 * empty labelled field.
 */
export function deskNotice(outcome, ref = "", hour = null) {
  const r = String(ref || "").trim();
  if (outcome === "ticketed") {
    const when = ticketTimeline(hour);
    const named = r ? ` as ${r}` : "";
    return when
      ? `I could not fix this one myself, so I have written it down for our team${named}. ${when}`
      : `I could not fix this one myself, so I have written it down for our team${named}. It is on today's list.`;
  }
  if (outcome === "owner") {
    return "This one needs Lala herself, so I have put it in front of her. Nothing has been changed on your account.";
  }
  return "";
}

// ---------------------------------------------------------------------------
// >>> **HER TIMELINE LAW: 4 PM PACIFIC (2026-08-14).** <<<
//
// Her words: *"tickets before 4 PM Pacific: 'this is in for tonight's update'; after 4 PM: 'this goes
// into tomorrow's update.'"* It is the one sentence on this desk that makes a PROMISE about when
// something will happen, which is why it is a declared law with its own hour rather than a phrase a
// model is asked to remember — a model that guessed *"tonight"* at 11pm has told a member something
// the house will not do, and she reads it as the house being unreliable rather than as a drafting
// slip (R141: a receipt for something nobody will witness).
// ---------------------------------------------------------------------------

/** The hour her working day closes, in her own zone. One declaration, read by the law and the gate. */
export const TICKET_CUTOFF_HOUR = 16;

export const TICKET_TONIGHT = "This is in for tonight's update.";
export const TICKET_TOMORROW = "This goes into tomorrow's update.";

/**
 * **WHICH UPDATE A TICKET FILED NOW IS IN.** The HOUR is a PARAMETER and this module resolves no
 * clock of its own: `src/` may not import `server/daily-morning.js`, and more to the point a law that
 * read `Date.now()` could not be driven over all twenty-four hours without a fake clock (R46's
 * `doFetch`-is-a-parameter pattern). The server supplies her Pacific hour through the house's ONE
 * zone declaration, so there is no second answer to what time it is where she is.
 *
 * >>> **AN UNREADABLE HOUR PROMISES NOTHING, AND THAT DIRECTION IS THE RULING (R18/R19).** <<< A
 * clock this house could not read is not a reason to guess at a night: the notice falls back to the
 * vaguer sentence it has always had, which is true whatever the hour. Promising *tonight* on a
 * coerced `Number(true)` is the one failure here that lands in front of a member (R136/R137), so only
 * a real integer hour inside a real day answers at all.
 */
export function ticketTimeline(hour) {
  if (typeof hour !== "number" || !Number.isInteger(hour) || hour < 0 || hour > 23) return "";
  return hour < TICKET_CUTOFF_HOUR ? TICKET_TONIGHT : TICKET_TOMORROW;
}

/**
 * **THE DESK MAY NEVER TOUCH MONEY OR AN ACCOUNT, AND THAT IS HELD AS A ROSTER RATHER THAN A
 * PROMISE.** Her SD2 fences: never promises refunds, never touches payment actions, never makes
 * account changes. The outcome above routes those conversations to her — this is the second wall,
 * asked of the ACT rather than of the message, because a member can talk her way from a how-do-I
 * question into a refund ask inside one conversation and the category was decided at the top of it.
 *
 * **INVERTED ON PURPOSE (I3's shape, THE COLOUR LAW's shape):** a banned list is green the day
 * somebody adds a new capability, and an ALLOWED list goes red on the same edit. The desk's tools
 * are named here and nowhere else.
 */
export const DESK_ALLOWED_ACTS = ["answer", "file_ticket", "flag_for_owner"];
export const deskActAllowed = (act) => DESK_ALLOWED_ACTS.includes(String(act || ""));

// ---------------------------------------------------------------------------
// SD2 — **THE DESK'S OWN VOICE: THE ASK LALA INTELLIGENCE WEARING A SUPPORT ROLE.**
//
// Her clause: *tapping Technical Support opens a support CONVERSATION, chat-style in the house look,
// the Ask Lala intelligence wearing a support role, HEAVILY GATED — every existing Lala gate rides
// (voice, confidentiality, no machinery talk, no judgment, tier laws) PLUS support fences: never
// discusses internals, never promises refunds or account changes, never touches payment actions,
// stays on helping the member use the house.*
//
// >>> **MEASURED BEFORE A LINE WAS WRITTEN (R25/R180), AND HER CLAUSE 4 ALREADY DECIDED WHERE THE
// CONVERSATION LIVES.** <<< *"The member gets the thread in her INBOX so she holds the record and can
// reply, replies continuing the same logged conversation."* The inbox is LR1's own DM machinery,
// `sendSupportReply` already delivers through the room's `sendMessage`, and `member_service_log` is
// already the append-only history. **So SD2 is not a new chat surface — it is the ANSWER.** Building
// a second transcript beside her inbox would be a second record of what this house has said to a
// member (R28/R29), and the one that drifts is the one her audit reads.
//
// >>> **AND A LIVE STANDING ORDER OF HERS GOVERNS THIS GROUND: INBOX-SILENCE (2026-08-08) — *LALA
// DOES NOT ANSWER THE INBOX*.** <<< That order is about a member DMing her account, which is not a
// request for a machine; SD2 is about a member who tapped **Technical Support** and described a
// broken page, which is. The two are compatible and neither is weakened here — but the answers land
// in the SAME inbox, so the desk gets **its own arming flag rather than reusing
// `LALA_INBOX_AUTOREPLY`** (R18: two questions), it **FAILS CLOSED**, and it is **OFF until she says
// the word**, which is the same ruling she has applied to every house voice reaching real members.
export const DESK_ANSWER_ENV = "SUPPORT_DESK_AUTOANSWER";
export function deskAutoAnswerOn(env = {}) {
  return String(env[DESK_ANSWER_ENV] || "").trim().toLowerCase() === "on";
}
/** Why a conversation was triaged and recorded but not answered. In her words, for her dashboard. */
export const DESK_SILENCED_REASON = "owner-silenced";

/**
 * **THE SUPPORT FENCES, AS AN ADDENDUM TO THE SYSTEM PROMPT RATHER THAN A SECOND PROMPT.** Her clause
 * says *every existing Lala gate rides*, so the desk speaks through the house's one pipeline and this
 * is what is ADDED — the same shape `INBOX_SYSTEM_ADDENDUM` already has for ITEM 12's door.
 *
 * **IT NAMES WHAT THE DESK MAY DO, NOT ONLY WHAT IT MAY NOT.** A prompt that is all prohibition
 * produces a voice that refuses and offers nothing, which is the PATHS PROTOCOL cruelty ITEM 5 was
 * written about: a member walled after being invited. Her clause (a) is *answer warmly and
 * completely, teach where the thing lives*, and that is the first line here.
 */
export const DESK_SYSTEM_ADDENDUM = [
  "You are answering a message somebody sent through Technical Support. You are the house's support desk.",
  "",
  "WHAT YOU ARE FOR: helping her use this house. If she is lost, tell her plainly where the thing lives and how to get to it. Answer completely, in one go, warmly. If it is a how-do-I question, that is the whole job and there is nothing to escalate.",
  "",
  // HER SCOPE CLAUSE, IN THE PROMPT AS WELL AS IN THE VERDICT. The `scope` field is what the code
  // reads; this is what stops the draft being written in the first place.
  "WHAT THIS DESK IS NOT: it is not Lala and it is not the house. You do not give readings, you do not interpret charts or cards or dreams, you do not give spiritual guidance, and you do not make conversation. If somebody asks you for any of that, say ONE polite line and close it. Do not answer the question even partly, do not offer to pass it on, and do not invite them to keep talking. Report the scope as off-topic and the house will add the closing line itself.",
  "",
  // HER REGISTER, and the two shapes she named by hand.
  "HOW YOU SOUND: polite, brief, human. Quick answers. No wordy paragraphs, no preamble, no sign-off. Say the thing, say where it lives, stop. If somebody is thanking you and asking nothing, answer in one short line and nothing more.",
  "",
  // CANCEL-1 (2026-09-03) — **THE EXAMPLE TAPS WERE NOT THE REAL TAPS, ON THE ONE CONTROL MEMBERS
  // WERE WRITING IN ABOUT.** *"then your account, and cancel is right there"* named a cancel button
  // sitting in the account screen. It is not there and never was: the account menu offers **Manage
  // billing**, which opens Stripe's own screen, and until this morning that screen had cancelling
  // switched off entirely. The example a model copies has to be a path that exists.
  "TEACH THE PATH: when she asks how to do something, give her the taps. \"Tap Member at the top, then Manage billing, and cancelling is on that screen.\" Name the controls she will see on her own screen.",
  "",
  // >>> **THE ROOMS ARE HANDED OVER RATHER THAN REMEMBERED, AND THEY ARE DERIVED (R11/R31).** <<< A
  // desk asked to recall a site it has never seen writes "or" three times and then invents an
  // address. `ROOM_LABELS` is the same roster the shelves and the room headings read, so a renamed
  // room reaches this prompt without anybody editing it.
  `THE ROOMS OF THIS HOUSE, BY THE NAMES SHE SEES: ${DESK_ROOM_NAMES}. The account controls live behind Member at the top of every page. Membership and billing live on the membership screen.`,
  "",
  "THERE IS NO DELETE CONTROL IN THIS HOUSE. Nothing on any screen deletes an account or a profile — not Member, not the account screen, nowhere. If she asks how to delete, never send her looking for a button: say plainly that there is no control for it on her screen, ask her what she wants removed, and say you are putting it in front of Lala herself.",
  "",
  "NEVER WRITE A WEB ADDRESS. Not a link, not a domain, not a slash-path. You do not know what this site's addresses are and a wrong one is worse than no answer. Name the taps instead. If you truly do not know the path, say what you do know and say plainly that you are writing it down, rather than guessing at a menu.",
  "",
  "ASK IF YOU MUST: if you genuinely cannot solve it without knowing one more thing, ask for that one thing. Keep the question about the site.",
  "",
  "IF YOU CANNOT SOLVE IT: say so plainly and say that it has been written down for the team. Do not guess at a cause, do not invent a timeline, and do not tell her to try again unless trying again is really the fix.",
  "",
  "WHEN SHE SAYS LALA WAS DOWN, OR A ROOM SAID TO TRY AGAIN LATER (her ruling, 2026-09-17):",
  `Say this, in these words: "${IMPROVING_NOTICE}"`,
  "Say it is done rather than that somebody is looking into it, tell her to refresh, and invite her to carry on where she left off. NEVER name a reason involving money, credits, a balance, a bill, a limit, a quota or any company this house buys from. She does not need to know what happened behind the wall, and telling her buys her nothing and costs the house her confidence.",
  "",
  "WHAT YOU MAY NEVER DO HERE:",
  "- Never discuss how this house is built: no code, no tables, no vendors, no internal names, no gates, no jobs, no error text.",
  "- Never promise a refund, a credit, a charge reversal, a price change or anything else about money. You cannot see her billing and you cannot act on it.",
  "- Never give a MONEY OR VENDOR reason for anything being down. Not credits, not a balance, not a limit, not a quota, not the name of a company this house buys from. Her ruling of 2026-09-17: frame it as the house improving and move her forward.",
  "- Never promise or make an account change: no tier, no email, no deletion, no password, no access.",
  "- Never tell her what somebody else's account shows.",
  "If her message is about any of those, say only that you are putting it in front of Lala herself and that nothing on her account has been changed.",
].join("\n");

/**
 * **THE FENCES AGAIN AS A DETERMINISTIC FLOOR, BECAUSE A PROMPT PREVENTS AND A GATE ENFORCES** (THE
 * GATE LAW). The addendum above is the prevention; this is what refuses a draft that crossed anyway.
 *
 * **IT IS SCOPED TO A PROMISE, NOT TO A WORD** (R19's direction, and the catalog round's own lesson
 * where `\britual\b` convicted *spiritual*): a member may legitimately be told *"Lala handles refunds
 * herself"*, and a law banning the word `refund` would refuse the honest sentence her clause (c)
 * requires. What is refused is the desk speaking in the FIRST PERSON about doing it.
 */
export const DESK_MONEY_PROMISE_RE =
  /\b(?:i|we)\s+(?:have\s+|has\s+|'ve\s+|will\s+|'ll\s+|can\s+|am\s+going\s+to\s+|going\s+to\s+)?(?:refund(?:ed)?|credit(?:ed)?|reverse[ds]?|cancel(?:led|ed)?|charge[ds]?|bill(?:ed)?|upgrade[ds]?|downgrade[ds]?|delete[ds]?|clos(?:e|ed)|reset)\b/i;

/**
 * >>> **THE DESK MAY NOT WRITE A WEB ADDRESS, AND THIS FENCE WAS PAID FOR BY A LIVE PROBE BEFORE SHE
 * ARMED ANYTHING (2026-08-14).** <<<
 *
 * Her clause 3 asks for the taps: *"Tap Member at the top, then your account, cancel is right
 * there."* Asked how to cancel, the desk drafted *"try going to **12thhouse.com/account** directly"*
 * — **a domain this house does not own and a page that does not exist**, told to a member as a place
 * to go. That is the fabrication class, on the one surface whose whole job is to be trusted.
 *
 * **A URL IS THE ONE THING THIS DESK CAN INVENT THAT LOOKS EXACTLY LIKE HELP**, so it is refused
 * deterministically rather than discouraged: the taps are always available to it and an address never
 * is. It is scoped to something that looks like a WEB address, so a member's own email address quoted
 * back to her is untouched.
 */
// **AND THE HOUSE'S OWN SUPPORT ADDRESS IS NOT A URL, WHICH THE FIRST DRAFT CONVICTED (R19).**
// `admin@12thhouse.ai` ends in a domain, so a bare domain match refused the ONE address S-5 requires
// this house to be able to give a member. The lookbehind is what tells an address from a link.
export const DESK_URL_RE = /\bhttps?:\/\/|(?<![@\w.])(?:www\.)?[a-z0-9-]+\.(?:com|ai|net|org|io|co)\b/i;

/** Machinery a member may never be shown. S1-A/R117's vocabulary, scoped to this door's own risk. */
export const DESK_INTERNALS_RE =
  /\b(?:postgres|sql|database table|migration|stack trace|null pointer|500 error from|our server logs|gate_log|railway|stripe api|endpoint|webhook|deploy(?:ed|ment)?)\b/i;

/**
 * >>> **THE DESK MAY NOT TEACH A DELETE CONTROL, BECAUSE THIS HOUSE HAS NEVER HAD ONE, AND THIS FENCE
 * WAS PAID FOR BY THE FIRST MEMBER WHO ASKED (#189, 2026-09-03).** <<<
 *
 * Shanah wrote *"Need to delete..how is this done"* four minutes after signing up, and the desk
 * answered: *"Tap Member at the top of any page, go to your account settings, and look for the option
 * to delete your account from there."* **There is no such option.** No surface in this house deletes
 * a member — not hers, not the owner's; migration 057 said in as many words that this would be
 * discovered *"the first time somebody asked to be deleted"*, and it was.
 *
 * **THIS IS THE `DESK_URL_RE` CLASS EXACTLY: the desk inventing the one thing that looks precisely
 * like help.** That fence refuses an invented ADDRESS; nothing refused an invented CONTROL. A member
 * sent hunting a button that does not exist is worse off than one told the truth, because she spends
 * her evening believing the house is hiding it from her.
 *
 * **IT IS SENTENCE-SCOPED SO THE TRUTHFUL SENTENCE SURVIVES** (R19: scope to the claim, never to the
 * word). *"There is no button on your screen for deleting an account"* names the same control and the
 * same act and is the sentence this desk SHOULD write, so a draft is convicted only where ONE
 * sentence names account-deletion, names a control, and carries no negator. Deleting the things that
 * really can be deleted — a journal entry, a bookmark, a saved seal — never mentions an account and
 * is untouched.
 */
export const DESK_ACCOUNT_DELETE_RE =
  /\b(?:delet\w*|remov\w*|eras\w*)\b[^.\n]{0,40}\b(?:account|profile)\b|\b(?:account|profile)\b[^.\n]{0,30}\b(?:delet\w*|remov\w*|eras\w*)\b/i;
/** A control she is being sent to look at. The taps clause's own vocabulary, which is why it is here. */
export const DESK_CONTROL_RE =
  /\b(?:tap|taps|click|go to|head to|open|look for|find|button|option|toggle|switch|menu|settings|screen|page)\b/i;
/** Any denial in the same sentence. The truthful line is the one that carries one. */
export const DESK_NEGATOR_RE =
  /\b(?:no|not|never|none|nothing|cannot|can't|isn't|aren't|won't|don't|doesn't)\b/i;

/** True where ONE sentence teaches a delete control this house does not have. */
export function deskInventsDeleteControl(text = "") {
  return String(text ?? "").split(/(?<=[.!?])\s+|\n+/).some((sentence) => (
    DESK_ACCOUNT_DELETE_RE.test(sentence)
    && DESK_CONTROL_RE.test(sentence)
    && !DESK_NEGATOR_RE.test(sentence)
  ));
}

/**
 * **WHAT THE DESK'S DRAFT IS REFUSED FOR.** Returns the violations by name, so a red says WHICH fence
 * — the shape every gate in this house already has (`xViolations` + `enforceX`).
 *
 * **AN OWNER-FLAGGED CONVERSATION IS HELD TO THE HARDEST VERSION**, because that is the one where the
 * member asked about money: on that outcome the desk may not speak in the first person about acting
 * at all, even to deny it in a way that sounds like a decision.
 */
export function deskDraftViolations(text = "", outcome = "answered") {
  const s = String(text ?? "");
  const bad = [];
  // >>> **AN OFF-TOPIC CLOSE HAS NO DRAFT TO REFUSE, AND ASKING THIS OF ONE WAS THIS ROUND'S OWN
  // TRAP.** <<< The house says its own single line there, so an empty reply is the CORRECT shape —
  // refusing it as `empty` would route every politely-closed off-topic ask onto her desk, which is
  // precisely the pile her clause 5 exists to empty.
  if (outcome === "closed") return bad;
  if (!s.trim()) bad.push("empty");
  // HER REGISTER, at the wall rather than at the target. See the two numbers above.
  if (s.length > DESK_REPLY_HARD_MAX) bad.push("far longer than a support answer");
  if (DESK_MONEY_PROMISE_RE.test(s)) bad.push("promises an action the desk cannot take");
  if (DESK_INTERNALS_RE.test(s)) bad.push("names the machinery");
  if (DESK_URL_RE.test(s)) bad.push("sends her to a web address rather than naming the taps");
  if (deskInventsDeleteControl(s)) bad.push("teaches a delete control this house does not have");
  if (outcome === "owner" && /\b(?:refund|credit|charge)\w*\b/i.test(s) && !/\bLala\b/.test(s)) {
    bad.push("talks about money without handing it to her");
  }
  return bad;
}

/**
 * >>> **HER REGISTER CLAUSE AS TWO NUMBERS, BECAUSE THEY ARE TWO FACTS (R18, 2026-08-14).** <<<
 *
 * *"Polite, brief, human. Quick answers, no wordy paragraphs."* — so the number the desk is ASKED for
 * came down from 1,200 characters (roughly two hundred words, which is a wordy paragraph by any
 * reading) to something the size of her own example: *"Tap Member at the top, then your account,
 * cancel is right there."*
 *
 * **AND THE FENCE IS NOT THE SAME NUMBER, WHICH IS DELIBERATE.** A draft refused for length becomes
 * HER work (`deskDraftViolations` routes to the owner), so a fence set at the target would put every
 * slightly-long-but-correct answer on her desk — over-escalation at volume, which is R19's cheap
 * direction turned expensive by repetition. The fence is where a reply stops being a support answer
 * at all, and the target is what the model is told.
 */
export const DESK_REPLY_MAX_CHARS = 700;
export const DESK_REPLY_HARD_MAX = 1600;

/**
 * **THE DESK'S TOOL, AND ITS SECOND FIELD IS THE ONE THE WHOLE TRIAGE LAW TURNS ON.**
 *
 * `resolved` is the model's own honest report of whether it SOLVED the thing, and `deskOutcome` reads
 * exactly it to choose between her (a) and her (b). It is described as conservatively as ITEM 12's
 * `confident` for the same reason: over-claiming here closes a real defect as answered and nobody is
 * ever told, which is the silent direction (R19/R8-4).
 *
 * **IT IS FORCED (`tool_choice`) AT THE CALL SITE**, so a model that would rather write prose still
 * hands back a structured verdict — and a missing tool block is a REFUSAL rather than a reply parsed
 * out of free text.
 */
export const DESK_TOOL = {
  name: "answer_support_message",
  description:
    "Read one message somebody sent through Technical Support and write the house's answer, plus an honest report of whether you actually solved it.",
  input_schema: {
    type: "object",
    properties: {
      resolved: {
        type: "boolean",
        description:
          "true ONLY if your reply really answers the thing she asked and she needs nothing further. If she is describing something broken that you cannot fix from here, this is false, even when your reply is helpful.",
      },
      // >>> **HER SCOPE CLAUSE AS A FIELD, WHICH IS WHAT MAKES IT STRUCTURAL RATHER THAN A PHRASE.**
      // <<< The verdict is the model's, judged on the SHAPE of what was asked; the CLOSING LINE is
      // the house's and is appended by `deskNotice`'s sibling rather than drafted, so an off-topic
      // ask cannot be closed with a sentence that quietly answers half of it.
      scope: {
        type: "string",
        enum: DESK_SCOPES,
        description:
          "\"site\" if this is about using 12th House: how to do something, where something lives, an account or subscription mechanic, a payment problem, or something that looks broken. \"off-topic\" for anything else at all, and especially for a reading, a chart or card interpretation, spiritual guidance, or conversation. When it is off-topic, leave the reply EMPTY: the house says its own one line.",
      },
      why: { type: "string", description: "One short sentence for the owner's desk only. The member never sees it." },
      reply: {
        type: "string",
        description: `The house's answer, warm and BRIEF, at most ${DESK_REPLY_MAX_CHARS} characters and usually far less. Teach her where the thing lives, in the taps she will see. No preamble and no sign-off. Never mention money, billing, accounts, refunds or anything about how the site is built, and never use an em dash.`,
      },
    },
    required: ["resolved", "scope", "why", "reply"],
  },
};

/**
 * **WHAT THE DESK'S OWN ENTRY SAYS ON HER AUDIT TRAIL.** Beside `SUPPORT_REPLY_SUMMARY`, and
 * DIFFERENT from it on purpose (R18): *she* answered and *the desk* answered are two facts about who
 * spoke, and a history that cannot tell them apart is one she cannot audit. It names no machinery.
 */
export const SUPPORT_DESK_SUMMARY = "The support desk answered a message she sent in.";

// ---------------------------------------------------------------------------
// SD6 — **THE GENTLE REDIRECT, AND WHAT MAKES A POST SUPPORT-SHAPED.**
//
// Her clause: *a gentle standing redirect for support-shaped Living Room posts, on existing
// moderation surfaces.*
// ---------------------------------------------------------------------------

/**
 * **THE ONE LINE THE MEMBER IS SENT, DECLARED ONCE.**
 *
 * **IT APOLOGISES FOR NOTHING AND CORRECTS NOBODY**, which is the whole of *gentle*: she has not
 * broken a rule, she is in the wrong room. It names WHERE to go rather than what she did, and it
 * leaves her post exactly where she put it — so nothing about this act is a moderation act.
 */
export const SUPPORT_REDIRECT_LINE =
  "I saw your post in the Living Room and wanted to make sure it reaches the right place. "
  + "Anything that is not working belongs in Technical Support, at the bottom of the menu, "
  + "where it gets written down and looked at. Your post is still up, exactly where you left it.";

/** What her ledger records. Names no machinery, and says what really happened (S1-A/R117). */
export const SUPPORT_REDIRECT_SUMMARY = "She was pointed at Technical Support. Her post was not touched.";

/**
 * **WHAT SHE IS SHOWN AS *SUPPORT-SHAPED*, AND IT IS A HINT ON HER SCREEN RATHER THAN AN ACT.**
 *
 * >>> **NOTHING IS SENT AUTOMATICALLY, AND THAT IS A RULING RATHER THAN CAUTION (R19).** <<< A
 * detector that redirected by itself would DM a member for describing a broken thing in a sentence
 * that happened to trip a regex — the room's own false-positive floor, one surface over, where the
 * cost is a stranger being told she is in the wrong place when she was chatting. Her word is
 * *standing redirect*, which is a control that stands there; the tap is hers.
 *
 * **THE VOCABULARY IS `preClassify`'s OWN, IMPORTED RATHER THAN RE-TYPED** (R28/R29/R31): a second
 * breakage regex is a second answer to *what a support message looks like*, and the one that drifts
 * is the one deciding whether a member gets pointed anywhere.
 *
 * **AND IT IS DELIBERATELY QUIET ON THE THREE FORCED LANES.** A post carrying distress or money
 * language is not something to point at a support form — that is hers, by her clause (c), and a
 * cheerful *try Technical Support* on top of it is the worst sentence in this file.
 */
export function supportShapedPost(text = "") {
  const s = String(text ?? "");
  if (!s.trim()) return false;
  if (preClassify(s)) return false;
  return BREAKAGE_RE.test(s);
}

// ---------------------------------------------------------------------------
// DESK-LAW-1 — **HER 48-HOUR PROMISE, HELD AS ARITHMETIC RATHER THAN AS A SENTENCE IN A RUNBOOK.**
//
// Her standing ruling, written into item 10 and binding on every window after it: *"no member waits
// past 48 hours without a real answer, the backlog count rides every morning scoreboard."*
//
// **THE FIRST HALF HAD AN INSTRUMENT AND THE SECOND HALF HAD NOTHING.** `deskBacklog` could already
// say who was waiting — it is how c188 and c162 were found — but it lived in `scripts/`, and the
// only thing that ran it was the overnight hunter, which files its findings into `RUNBOOK.md`. **A
// law whose only reader is a file she does not open in the morning is a law nothing asks** (R38/R165,
// and it is the same failure the desk backlog's own header names: *a queue with no instrument on it*).
// So the count joins the list she already reads first — *What needs you* — by EXISTING (R11/R31),
// rather than as a fourth box she has to learn.
//
// **THE THRESHOLD IS DECLARED ONCE, HERE, BECAUSE THREE THINGS ASK IT**: the reader that counts the
// breaches, the roster line that words them, and the lock that drives both. Three spellings of
// *forty-eight* would be three answers to whether this house kept its promise.
// ---------------------------------------------------------------------------

/** Her promise, in hours. One declaration; the reader, the wording and the lock all ask this one. */
export const DESK_ANSWER_HOURS = 48;

/**
 * How long a message has been waiting, in HOURS rather than in whole days.
 *
 * >>> **WHY NOT REUSE `backlogAgeDays`.** <<< The backlog's own age is floored to whole days, so a
 * member who wrote in 47 hours ago reads as *1 day* and one who wrote in 49 hours ago also reads as
 * *2 days* — and the second of those is a broken promise while the first is not. **Her law is in
 * hours, so the arithmetic that judges it must be** (R18: the two facts get two spellings). The days
 * figure survives untouched for the CLI's list, which is a workload rather than a verdict.
 *
 * `null` for an unreadable date, which is NOT zero: an undated row is a row this house cannot judge,
 * and counting it as *fresh* would quietly shrink the breach count (R25/R73).
 *
 * >>> **`nowMs` IS REQUIRED AND HAS NO DEFAULT, WHICH THIS FILE'S OWN STANDING LAW ALREADY DEMANDED.**
 * <<< `sd-support-desk.mjs` holds that *"the law itself resolves NO clock, so a gate can drive it over
 * hours this house is not currently in"* — R46's parameter pattern — and a `= Date.now()` default was
 * the first draft here until that clause caught it. It is the right law twice over on this one: the
 * whole point of the arithmetic is to judge a promise against a moment, and a law that reads the wall
 * clock itself can only ever be driven at the one moment the gate happens to run.
 */
export function deskWaitHours(at, nowMs) {
  const ms = new Date(at).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, (nowMs - ms) / 3600000);
}

/**
 * **HAS THIS ONE BROKEN THE PROMISE?** `>=` rather than `>`, deliberately: at exactly forty-eight
 * hours the promise is spent, and R19 says which direction an error is allowed to go — a house that
 * rounds this one in its own favour is a house that stops finding the member it left waiting.
 *
 * An unreadable wait is NOT a breach. It is unknown, and unknown never counts as either verdict.
 */
export const deskPromiseBroken = (hours) => typeof hours === "number" && Number.isFinite(hours) && hours >= DESK_ANSWER_HOURS;
